import { submitSubscription, KlaviyoSubscriptionError } from '@theme/kb-klaviyo-client';

const STATE_IDLE = 'idle';
const STATE_LOADING = 'loading';
const STATE_SUCCESS = 'success';
const STATE_ERROR = 'error';
const STATE_INVALID = 'invalid';

const SIGNUP_EVENT = 'kb:waitlist:signup';
const EMAIL_STORAGE_KEY = 'kbWaitlistEmail';

class KBWaitlistForm extends HTMLElement {
  #controller = new AbortController();
  #state = STATE_IDLE;

  connectedCallback() {
    const { signal } = this.#controller;
    const form = this.#form;
    if (!form) return;

    form.addEventListener('submit', this.#handleSubmit, { signal });
    this.addEventListener('kb:reset', this.#reset, { signal });

    const input = this.#input;
    if (input) {
      input.addEventListener(
        'input',
        () => {
          if (this.#state === STATE_INVALID || this.#state === STATE_ERROR) {
            this.#setState(STATE_IDLE);
          }
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get source() {
    return this.dataset.source || 'unknown';
  }

  /**
   * Acquisition source sent to Klaviyo as signup_source.
   * Reads the `source` (then `utm_source`) query parameter from the page URL,
   * falling back to the form's own data-source when no parameter is present.
   * Applied to every form so a later SMS submit re-affirms the same value
   * instead of overwriting the source set by the email submit.
   */
  get signupSource() {
    const searchParams = new URLSearchParams(window.location.search);
    const sourceFromUrl = searchParams.get('source') || searchParams.get('utm_source');
    const trimmedSource = sourceFromUrl ? sourceFromUrl.trim() : '';
    return trimmedSource || this.source;
  }

  /**
   * Funnel position of this form, sent to Klaviyo as form_location.
   * Distinct from signupSource (the acquisition channel). The bottom form's
   * data-source is 'final' in code; it is mapped to 'bottom' to match the
   * analytics vocabulary (hero | sticky | bottom).
   */
  get formLocation() {
    return this.source === 'final' ? 'bottom' : this.source;
  }

  /**
   * UTM parameters read from the page URL, sent to Klaviyo as individual
   * profile properties. Only parameters actually present (and non-empty) are
   * included, so an absent parameter never overwrites an existing profile value.
   */
  get utmParameters() {
    const searchParams = new URLSearchParams(window.location.search);
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    const parameters = {};
    for (const key of keys) {
      const value = searchParams.get(key);
      const trimmed = value ? value.trim() : '';
      if (trimmed) parameters[key] = trimmed;
    }
    return parameters;
  }

  get listId() {
    return this.dataset.klaviyoList || '';
  }

  get smsListId() {
    return this.dataset.klaviyoSmsList || '';
  }

  get companyId() {
    return this.dataset.klaviyoKey || '';
  }

  get inputType() {
    return this.dataset.inputType === 'tel' ? 'tel' : 'email';
  }

  get redirectOnSuccess() {
    return this.dataset.redirectOnSuccess || '';
  }

  get #form() {
    const form = this.querySelector('form');
    return form instanceof HTMLFormElement ? form : null;
  }

  get #input() {
    const input = this.querySelector('input[name="email"], input[name="phone"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  get #button() {
    const button = this.querySelector('button[type="submit"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  get #message() {
    return this.querySelector('[data-kb-message]');
  }

  get #successMessage() {
    return this.querySelector('[data-kb-success]');
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    event.preventDefault();
    if (this.#state === STATE_LOADING) return;

    const input = this.#input;
    if (!input) return;

    const raw = input.value.trim();
    if (!raw) {
      this.#setState(STATE_INVALID, this.dataset.emptyMessage || '');
      input.focus();
      return;
    }

    const customProperties = { ...this.utmParameters };

    const payload = {
      source: this.signupSource,
      listId: this.listId,
      companyId: this.companyId,
      customProperties,
    };

    if (this.inputType === 'tel') {
      const phone = this.#normalizePhone(raw);
      if (!/^[\d+\s()\-]+$/.test(raw) || !/^\+\d{7,15}$/.test(phone)) {
        this.#setState(STATE_INVALID, this.dataset.invalidMessage || '');
        input.focus();
        return;
      }
      payload.phone = phone;
      if (this.smsListId) payload.listId = this.smsListId;
      const storedEmail = this.#getStoredEmail();
      if (storedEmail) payload.email = storedEmail;
      // SMS opt-in records its own location key so it does not overwrite the
      // entry-point form_location the email form set on the same profile.
      customProperties.sms_form_location = this.source;
    } else {
      payload.email = raw;
      customProperties.form_location = this.formLocation;
    }

    this.#setState(STATE_LOADING);

    try {
      await submitSubscription(payload);
      this.#setState(STATE_SUCCESS);

      if (payload.email && this.inputType !== 'tel') {
        try {
          sessionStorage.setItem(EMAIL_STORAGE_KEY, payload.email);
        } catch (_) {
          // sessionStorage unavailable; ignore
        }
      }

      this.#emitSignup(payload);

      const redirect = this.redirectOnSuccess;
      if (redirect) {
        const url = new URL(redirect, window.location.origin);
        url.searchParams.set('source', this.signupSource);
        if (payload.email) url.searchParams.set('email', payload.email);
        window.location.assign(url.toString());
      }
    } catch (error) {
      console.error('[kb-waitlist-form] submit failed', error);
      if (error instanceof KlaviyoSubscriptionError && error.field) {
        const invalidMessage = this.dataset.invalidMessage || '';
        this.#setState(STATE_INVALID, invalidMessage);
        input.focus();
        return;
      }
      this.#setState(STATE_ERROR);
    }
  };

  /**
   * @param {string} raw
   */
  #normalizePhone(raw) {
    const trimmed = raw.replace(/[^\d+]/g, '');
    if (trimmed.startsWith('+')) return trimmed;
    if (trimmed.startsWith('0')) return `+44${trimmed.slice(1)}`;
    if (trimmed.length > 0) return `+${trimmed}`;
    return '';
  }

  #getStoredEmail() {
    try {
      return sessionStorage.getItem(EMAIL_STORAGE_KEY) || '';
    } catch (_) {
      return '';
    }
  }

  /**
   * @param {object} payload
   */
  #emitSignup(payload) {
    this.dispatchEvent(
      new CustomEvent(SIGNUP_EVENT, {
        bubbles: true,
        composed: true,
        detail: {
          // Emit the form identifier (hero/sticky/final/sms), not the resolved
          // Klaviyo source, so the thank-you modal keeps branching on 'sms'.
          source: this.source,
          email: payload.email,
          phone: payload.phone,
        },
      })
    );
  }

  /**
   * @param {string} state
   * @param {string} [customMessage]
   */
  #setState(state, customMessage) {
    this.#state = state;
    this.dataset.state = state;

    const button = this.#button;
    const input = this.#input;
    const message = this.#message;
    const success = this.#successMessage;

    if (button) {
      button.disabled = state === STATE_LOADING;
    }
    if (input) {
      input.readOnly = state === STATE_LOADING;
      if (state === STATE_SUCCESS) input.blur();
    }

    if (success) {
      success.textContent = state === STATE_SUCCESS ? this.dataset.successMessage || '' : '';
    }

    if (message) {
      if (state === STATE_ERROR) {
        message.textContent = this.dataset.errorMessage || '';
      } else if (state === STATE_INVALID) {
        message.textContent = customMessage || this.dataset.invalidMessage || '';
      } else {
        message.textContent = '';
      }
    }
  }

  #reset = () => {
    this.#setState(STATE_IDLE);
    if (this.#input) this.#input.value = '';
  };
}

if (!customElements.get('kb-waitlist-form')) {
  customElements.define('kb-waitlist-form', KBWaitlistForm);
}

export { KBWaitlistForm, SIGNUP_EVENT, EMAIL_STORAGE_KEY };
